import { useEffect, useRef, useState } from "react";
import { getCustomisationValue, getGroupData } from "../../EditablePreview";
import VisualisedNode, { IMAGE_MODES } from "../VisualisedNode";
import './PositionableProgressBar.scss';
import { NODE_LIST_CONTROLLER } from "../../../../../../NodeList/NodeList";

export const PROGRESS_BAR_STATE = {
    perc: 0.7,
}

const PositionableProgressBar = (props) => {
    // console.log("PositionableProgressBar props:", props);

    const progressBarGroupLinkage = props.node?.config?.groupSettings?.dataLink?.split('.');
    const progressBarGroupPath = progressBarGroupLinkage ? progressBarGroupLinkage[0] + '.' + progressBarGroupLinkage[1] : null;

    const positionSetting = getCustomisationValue(props.node.customisationVars, progressBarGroupPath ? progressBarGroupPath + '.position.position' : 'progressBar.shapeAndPosition.position.position');
    // console.log("Position setting:", positionSetting);
    const dir = (positionSetting === 'left' || positionSetting === 'right' || positionSetting === 'top-left' || positionSetting === 'top-right' || positionSetting === 'bottom-left' || positionSetting === 'bottom-right' || positionSetting === 'middle-left' || positionSetting === 'middle-right') ? 'vertical' : 'horizontal';

    const percBarWidth = (dir === 'horizontal' ? 1920 : 1080) * (getCustomisationValue(props.node.customisationVars, progressBarGroupPath ? progressBarGroupPath + '.shape.length' : 'progressBar.shapeAndPosition.shape.length') / 100) * props.previewScale;
    const percBarHeight = getCustomisationValue(props.node.customisationVars, progressBarGroupPath ? progressBarGroupPath + '.shape.thickness' : 'progressBar.shapeAndPosition.shape.thickness') * props.previewScale;

    let hasOverlayImages = false;
    let overlaysNode = null;
    let overlaysData = null;
    for (let i = 0; i < props.node.subNodes.length; i++) {
        const subNode = props.node.subNodes[i];
        if (subNode.config.type === 'group' && subNode.config.groupSettings.dataLink && (subNode.config.name.toLowerCase() === 'goals' || subNode.config.name.toLowerCase() === 'overlays' || subNode.config.name.toLowerCase() === 'goalimages' || subNode.config.name.toLowerCase() === 'overlayimages')) {
            hasOverlayImages = true;
            overlaysNode = subNode;
            overlaysData = getGroupData(props.node.customisationVars, overlaysNode.config.groupSettings.dataLink, -1);
            if (NODE_LIST_CONTROLLER.nodeListExists('customisation-workspace-left-list')) {
                NODE_LIST_CONTROLLER.getSubNodes('customisation-workspace-left-list', overlaysNode); // Ensure sub-nodes are loaded
            } else {
                NODE_LIST_CONTROLLER.getSubNodes('customisation-workspace-left-list_' + props.node?.featureData?._id, overlaysNode); // Ensure sub-nodes are loaded
            }
            break
        }
    }

    const [fillPerc, setFillPerc] = useState(props?.fillPerc ?? 0.7);
    
    const [progressBarPaused, setProgressBarPaused] = useState(false);
    const progressPausedRef = useRef(false);
    useEffect(() => {
        progressPausedRef.current = progressBarPaused;
    }, [progressBarPaused]);

    PROGRESS_BAR_STATE.perc = fillPerc;
    useEffect(() => {
        setFillPerc(props?.fillPerc ?? 0.7);
    }, [props?.fillPerc]);
    useEffect(() => {
        if (!hasOverlayImages) return;

        if (props.activeNode?.parent?.parent !== props.node && typeof props.activeNode?.dataIndex === 'number') {
            // We have an active node that is part of a multiple data set, so we need to check if this data has a percentage override
            const activeNodeChildren = props.activeNode?.config?.children || [];
            for (let i = 0; i < activeNodeChildren.length; i++) {
                const childFields = activeNodeChildren[i].fields;
                if (Array.isArray(childFields)) {
                    for (let j = 0; j < childFields.length; j++) {
                        const field = childFields[j];
                        if (field?.customFieldConfig && field.customFieldConfig.dataLink && field.customFieldConfig?.dataLink?.includes('completePerc')) {
                            const overridePerc = getCustomisationValue(props.activeNode.customisationVars, field.customFieldConfig.dataLink, props.activeNode.dataIndex ?? 0, null);
                            if (typeof overridePerc === 'number') {
                                setFillPerc(overridePerc / 100);
                                PROGRESS_BAR_STATE.perc = overridePerc / 100;
                                return;
                            }
                        }
                    }
                }
            }
        }

        // setFillPerc(-0.2);
        let unpauseTimeout = null;
        const interval = setInterval(() => {
            setFillPerc(prev => {
                if (typeof PROGRESS_BAR_STATE.fixedPerc !== 'number') {
                    if (progressPausedRef.current) return prev;

                    let newPerc = prev + 0.0075;
                    if (newPerc > 1.2) newPerc = -0.2;

                    // Have we reached an overlay point?
                    if (overlaysData && overlaysData.length > 0) {
                        for (let i = 0; i < overlaysData.length; i++) {
                            const overlay = overlaysData[i];
                            const overlayPerc = overlay?.completePerc / 100 ?? 0;
                            if (prev < overlayPerc && newPerc >= overlayPerc) {
                                // Pause for 1 second
                                newPerc = overlayPerc;
                                setProgressBarPaused(true);
                                unpauseTimeout = setTimeout(() => {
                                    setProgressBarPaused(false);
                                }, 1000);
                            }
                        }
                    }

                    PROGRESS_BAR_STATE.perc = newPerc;
                    return newPerc;
                }

                let newPerc = PROGRESS_BAR_STATE.fixedPerc + 0.0075;
                PROGRESS_BAR_STATE.perc = newPerc;
                return newPerc;
            });
        }, 50);
        return () => {
            setProgressBarPaused(false);
            clearTimeout(unpauseTimeout);
            clearInterval(interval);
        };
    }, [hasOverlayImages, props.activeNode]);

    return (
        <div className="positionable-progress-bar-holder" style={{margin: (props.margin ?? 50) * props.previewScale, width: '100%'}}>
            <VisualisedNode
                {...props}
                dir={dir}
                fillPerc={fillPerc < 0 ? 0 : fillPerc > 1 ? 1 : fillPerc}
                stripeWidth={30}
                node={props.node.subNodes[0]}
                style={
                    {
                        minWidth: dir === 'horizontal' ? percBarWidth : percBarHeight,
                        minHeight: dir === 'horizontal' ? percBarHeight : percBarWidth,
                    }
                }
            />
            {hasOverlayImages && overlaysData && overlaysData.length > 0 && overlaysData.map((overlay, index) => {
                // console.log("Rendering overlay image:", overlay);

                const perc = overlay?.completePerc ?? 0;

                const lockedImgData = overlay?.lockedFilename ?? null;
                const lockedScale = overlay?.lockedScale ?? 100;
                const lockedXOffset = overlay?.xOffsetLocked ?? 0;
                const lockedYOffset = overlay?.yOffsetLocked ?? 0;

                const unlockedScale = overlay?.unlockedScale ?? 100;
                const unlockedXOffset = overlay?.xOffsetUnlocked ?? 0;
                const unlockedYOffset = overlay?.yOffsetUnlocked ?? 0;
                const unlockedImgData = overlay?.unlockedFilename ?? null;

                const overlayImageStyle = {
                    left: dir === 'horizontal' ? `${perc}%` : `calc(0% + ${5 * props.previewScale}px)`,
                    top: dir === 'horizontal' ? `calc(0% + ${5 * props.previewScale}px)` : `${100 - perc}%`,
                    zIndex: 1,
                };
                const fillPercentage = fillPerc * 100;

                return (
                    <div
                        key={`progress-bar-overlay-image-${index}`}
                        className="progress-bar-overlay-image"
                        style={overlayImageStyle}
                    >
                        <VisualisedNode
                            {...props}
                            key={`progress-bar-overlay-visualised-node-${index}`}
                            node={overlaysNode.subNodes[index]}
                        >
                            {fillPercentage < perc && lockedImgData && (
                                <img
                                    src={lockedImgData.srcUrl}
                                    alt="Locked Overlay"
                                    style={{
                                        transform: `scale(${(lockedScale * props.previewScale) / 100})`,
                                        marginLeft: `${lockedXOffset * props.previewScale}px`,
                                        marginTop: `${lockedYOffset * props.previewScale}px`,
                                    }}
                                />
                            )}
                            {fillPercentage >= perc && unlockedImgData && (
                                <img
                                    src={unlockedImgData.srcUrl}
                                    alt="Unlocked Overlay"
                                    style={{
                                        transform: `scale(${(unlockedScale * props.previewScale) / 100})`,
                                        marginLeft: `${unlockedXOffset * props.previewScale}px`,
                                        marginTop: `${unlockedYOffset * props.previewScale}px`,
                                    }}
                                />
                            )}
                        </VisualisedNode>
                    </div>
                    
                );
            })}
        </div>
    )
}

export default PositionableProgressBar;
